/**
 * Script guards.
 *
 * Mapbox returns road NAMES verbatim from OpenStreetMap regardless of the
 * `language` parameter - asking for English still yields
 * "Turn right onto ถนนมหาจักรพรรดิ์". Two things go wrong when that reaches
 * an English-speaking driver:
 *
 *  1. The sign is unreadable.
 *  2. Android/iOS TTS sees Thai codepoints and silently switches to a Thai
 *     voice mid-sentence (or garbles the whole utterance). That is the
 *     "some voices are in Thai when English is selected" bug - the voice
 *     selection was never wrong, the TEXT was.
 *
 * So: nothing containing a script the active language can't read is ever
 * spoken or displayed. Road numbers (304, AH19) are script-neutral and are
 * what we fall back to.
 */

import type { Lang } from './i18n';

const THAI = /[฀-๿]/;
const LATIN = /[A-Za-z]/;

export function hasThai(s: string): boolean {
  return THAI.test(s);
}

/** True when `text` is safe to show/speak in `lang` without script mixing. */
export function readableIn(text: string, lang: Lang): boolean {
  if (!text) return false;
  return lang === 'th' ? !LATIN.test(text) || !THAI.test(text) : !THAI.test(text);
}

/**
 * Drop any word containing an unreadable script, keeping numbers and refs.
 * "304 / ถนนวงแหวน" -> "304". Returns '' when nothing survives.
 */
export function keepReadable(text: string, lang: Lang): string {
  if (!text) return '';
  const bad = lang === 'th' ? null : THAI;
  if (!bad) return text.trim();
  return text
    .split(/[\s/·,]+/)
    .filter((w) => w && !bad.test(w))
    .join(' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}
